'use client';

import { type Atom, atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { atomWithStorage } from 'jotai/utils';
import { useCallback } from 'react';

import { LOCAL_STORAGE } from 'lib/constants/local-storage';

import { DebugMessageColor, colorOption, locColor } from 'utils/debugger';

import { ROBOT_ROS_TOPICS, RobotRosTopic } from '../my-robot/relay-topics';

type DebugSignalRRelayStateEntry = {
  log: boolean;
  color: string;
};

type DebugSignalRRelayState = Record<RobotRosTopic, DebugSignalRRelayStateEntry>;

const getLogSignalRRelayRosTopicsDefaultState = (): DebugSignalRRelayState => {
  const stored =
    typeof window !== 'undefined'
      ? localStorage?.getItem(LOCAL_STORAGE.DEBUG.SIGNAL_R_RELAY)
      : null;
  const state = stored ? JSON.parse(stored) : {};

  ROBOT_ROS_TOPICS.forEach((topic) => {
    if (!state[topic]) {
      state[topic] = {
        log: false,
        color: colorOption[DebugMessageColor.BLUE],
      };
    }
  });

  return state;
};

const logSignalRRelayRosTopicsAtom = atomWithStorage(
  LOCAL_STORAGE.DEBUG.SIGNAL_R_RELAY,
  getLogSignalRRelayRosTopicsDefaultState(),
);

const logSignalRRelayRosTopicsSelector = atomFamily<
  RobotRosTopic,
  Atom<DebugSignalRRelayStateEntry>
>((topic) => {
  const innerAtom = atom((get) => get(logSignalRRelayRosTopicsAtom)[topic]);
  innerAtom.debugLabel = 'Debug/SignalR Relay Selector';
  return innerAtom;
});

export function useLogSignalRRelaySetter(key: RobotRosTopic) {
  const { color, log } = useAtomValue(logSignalRRelayRosTopicsSelector(key));
  const setDebugState = useSetAtom(logSignalRRelayRosTopicsAtom);

  const updateLog = (log: boolean) =>
    setDebugState((old) => {
      const newItem = {
        ...old[key],
        log,
      };
      return {
        ...old,
        [key]: newItem,
      };
    });

  const updateColor = (color: string) => {
    setDebugState((old) => {
      const newItem = {
        ...old[key],
        color,
      };
      return {
        ...old,
        [key]: newItem,
      };
    });
  };

  return { color, log, updateLog, updateColor } as const;
}

/**
 * Returns a callback to read the state without subscribing to it.
 */
function useDebugSignalRRelayCallback() {
  const getDebugSignalRRelay = useAtomCallback(
    useCallback(
      (get) => (topic: RobotRosTopic) => get(logSignalRRelayRosTopicsSelector(topic)),
      [],
    ),
  );

  return { getDebugSignalRRelay };
}

/**
 * Returns a callback to read the state without subscribing to it.
 */
export function useLogSignalRRelay() {
  const { getDebugSignalRRelay } = useDebugSignalRRelayCallback();

  function logSignalRRelay(topic: RobotRosTopic, text: string, ...rest: unknown[]) {
    const debug = getDebugSignalRRelay()(topic);

    if (debug?.log) {
      locColor(debug.color, text, ...rest);
    }
  }

  return { logSignalRRelay };
}
